import xeroLogo from "@/assets/logos/xero.svg";
import quickbooksLogo from "@/assets/logos/quickbooks.svg";
import freeagentLogo from "@/assets/logos/freeagent.svg";
import screenshotDashboard from "@/assets/desktop-screenshot-dashboard.webp";

import screenshotDispatch from "@/assets/desktop-screenshot-dispatch.webp";
import screenshotInvoices from "@/assets/invoice-management-desktop.webp";
import screenshotTracking from "@/assets/desktop-screenshot-tracking.webp";
import mobileDashboard from "@/assets/screenshot-driver-dashboard.webp";
import mobileDamage from "@/assets/screenshot-ai-damage.webp";
import mobileProof from "@/assets/screenshot-proof-delivery.webp";
import mobileInvoice from "@/assets/screenshot-invoice.webp";
import beepBeepLogo from "@/assets/beep-beep-transport-logo.webp";
import sprintLogo from "@/assets/sprint-vehicle-movements-logo.jpeg";
import amzLogo from "@/assets/amz-transport-logo.png";
import { TESTIMONIALS } from "./testimonials";

export interface Shot {
  title: string;
  desc: string;
  image: string;
  url?: string;
  variant: "browser" | "device";
  bullets?: string[];
}

export const desktopShots: Shot[] = [
  {
    title: "Admin Dashboard",
    desc: "A real-time command centre for active jobs, driver status, revenue and deliveries — everything visible on one screen.",
    image: screenshotDashboard,
    url: "transportmate.co.uk/dashboard",
    variant: "browser",
    bullets: ["Live job and driver status", "Revenue and delivery totals", "Jump straight into any job"],
  },
  {
    title: "Schedule & Dispatch",
    desc: "Assign drivers, manage routes and coordinate your fleet from a drag-and-drop schedule board.",
    image: screenshotDispatch,
    url: "transportmate.co.uk/schedule",
    variant: "browser",
    bullets: ["Drag-and-drop driver assignment", "Day, week and month views", "Drivers notified instantly"],
  },
  {
    title: "Invoice Management",
    desc: "Invoices generated on job completion and synced to QuickBooks, Xero or FreeAgent with payment tracking built in.",
    image: screenshotInvoices,
    url: "transportmate.co.uk/invoicing",
    variant: "browser",
    bullets: ["Automatic invoice generation", "Accounting software sync", "Paid / unpaid tracking"],
  },
  {
    title: "Live Fleet Tracking",
    desc: "Track every driver in real time with GPS positions, ETAs and delivery status — and share a link with your customer.",
    image: screenshotTracking,
    url: "transportmate.co.uk/driver-tracking",
    variant: "browser",
    bullets: ["Real-time driver positions", "Customer tracking links", "No app download for customers"],
  },
];

export const mobileShots: Shot[] = [
  {
    title: "Driver Dashboard",
    desc: "Drivers manage jobs, update status and navigate from a single mobile screen.",
    image: mobileDashboard,
    variant: "device",
  },
  {
    title: "AI Damage Scan",
    desc: "Photograph damage and get instant AI condition reports with severity ratings.",
    image: mobileDamage,
    variant: "device",
  },
  {
    title: "Proof of Delivery",
    desc: "GPS-stamped, time-verified reports with photos and digital signatures — sent automatically.",
    image: mobileProof,
    variant: "device",
  },
  {
    title: "Auto-Generated Invoice",
    desc: "Professional invoices created on completion and synced to your accounting software.",
    image: mobileInvoice,
    variant: "device",
  },
];

// Wording lives in ./testimonials (single source, also used by the static
// SEO plugin); logos are attached here for the visible cards only.
const testimonialLogos: Record<string, string> = {
  "Luke Anderson": beepBeepLogo,
  "Yasmine Khan": amzLogo,
  "Elliot Gale": sprintLogo,
};

export const testimonials = TESTIMONIALS.map((t) => ({ ...t, logo: testimonialLogos[t.name] }));

export { faqItems } from "./marketingFaq";

export const comparisonRows = [
  { feature: "AI Damage Detection", tm: "Built-in AI damage detection and condition reports", old: "Manual photos with no automated reporting" },
  { feature: "Proof of Delivery", tm: "Digital signatures with timestamp and GPS verification", old: "Paper forms or photo messages" },
  { feature: "Driver Job Management", tm: "Drivers receive jobs instantly on their mobile dashboard", old: "Jobs sent via messages and phone calls" },
  { feature: "Customer Tracking", tm: "Live tracking links automatically sent to customers", old: "Customers must call for updates" },
  { feature: "Invoice Generation", tm: "Invoices automatically created when jobs complete", old: "Manual invoicing after every delivery" },
  { feature: "Accounting Integration", tm: "Syncs with QuickBooks, Xero, and FreeAgent", old: "Manual data entry" },
  { feature: "Subcontract Management", tm: "Manage subcontractors, jobs, and payments automatically", old: "Manual coordination and payment tracking" },
  { feature: "Offline Support", tm: "Works without signal and syncs when back online", old: "Drivers cannot update jobs without signal" },
];

export const integrations = [
  { name: "QuickBooks", logo: quickbooksLogo, colour: "#2CA01C" },
  { name: "Xero", logo: xeroLogo, colour: "#13B5EA" },
  { name: "FreeAgent", logo: freeagentLogo, colour: "#049C8C" },
];

